import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import UseAxios from '../../../Api/http/http';
import { getPendingPositionsReport, addEditPendingPositionsReport } from '../../../Api/request/misReports';
import { getPendingPositionReportsExcel } from '../../../Api/request/reports';
import { format } from 'date-fns';
import { OOPS_TRY_AGAIN, UPDATE_SUCCESS, PENDING_POSITIONS_REPORT } from '../../../Constants/strings';

export function usePendingPositionsReport(initialDateRange) {
  const [pendingPositionsRows, setPendingPositionsRows] = useState([]);
  const [pendingPositionsPageSize, setPendingPositionsPageSize] = useState(20);
  const [pendingPositionsDateRange, setPendingPositionsDateRange] = useState({
    startDate: initialDateRange.startDate,
    endDate: initialDateRange.endDate,
    label: initialDateRange.label,
  });
  const [pendingPositionsData, setPendingPositionsData] = useState(null);

  const getPendingPositionsData = async (startDate, endDate, dealType) => {
    try {
      const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : '';
      const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : '';

      const response = await UseAxios(
        getPendingPositionsReport(
          formattedStartDate,
          formattedEndDate,
          dealType,
        ),
      );

      setPendingPositionsData(response?.data?.data);

      if (response?.data?.data?.pendingDetails) {
        const updatedDetails = response.data.data.pendingDetails.map(
          (item) => ({
            ...item,
            id: uuidv4(),
          }),
        );

        const totalsRow = getUpdatedRowsWithTotal(response);
        const updatedDataWithTotals = [...updatedDetails, totalsRow];
        const numberOfRecords = updatedDataWithTotals?.length || 20;

        setPendingPositionsPageSize(numberOfRecords);
        setPendingPositionsRows(updatedDataWithTotals);
      } else {
        setPendingPositionsPageSize(20);
        setPendingPositionsRows([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setPendingPositionsPageSize(20);
      setPendingPositionsRows([]);
      return { error: PENDING_POSITIONS_REPORT.FETCH_ERROR };
    }
  };

  const updatePendingPositionsData = async (payload) => {
    try {
      const response = await UseAxios(addEditPendingPositionsReport(payload));

      if (response?.status === 200) {
        const { startDate, endDate } = pendingPositionsDateRange;
        await getPendingPositionsData(startDate, endDate);
        return { success: true, message: UPDATE_SUCCESS };
      } else {
        console.error('Unexpected response status:', response?.status);
        return { success: false, message: OOPS_TRY_AGAIN };
      }
    } catch (error) {
      console.error('Error saving data:', error);
      return { success: false, message: OOPS_TRY_AGAIN };
    }
  };

  const downloadPendingPositionsExcel = async (startDate, endDate) => {
    try {
      await getPendingPositionReportsExcel(startDate || null, endDate || null);
      return { success: true };
    } catch (error) {
      console.error('Error downloading Excel report:', error);
      return {
        success: false,
        message: PENDING_POSITIONS_REPORT.DOWNLOAD_FAILURE_MESSAGE,
        severity: PENDING_POSITIONS_REPORT.ERROR_SEVERITY,
      };
    }
  };

  const getUpdatedRowsWithTotal = (response) => {
    return {
      id: 'Total',
      verticalName: 'Total',
      pendingPositions: response?.data?.data?.totalPendingPositions,
      pendingJd: response?.data?.data?.totalPendingJd,
      pendingCandidateIndentification:
        response?.data?.data?.totalCandidateIndentification,
      pendingInterviewFeedback: response?.data?.data?.totalInterviewFeedback,
    };
  };

  return {
    pendingPositionsRows,
    pendingPositionsData,
    pendingPositionsPageSize,
    pendingPositionsDateRange,
    setPendingPositionsDateRange,
    getPendingPositionsData,
    updatePendingPositionsData,
    downloadPendingPositionsExcel,
  };
}
